'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Host adapter contract for the video/series/channel surface.
 * Backend reality: this proxies through to Cinema8 (external video
 * platform) — the contract is shaped to match Cinema8's resource
 * model so a thin host implementation can pass requests through.
 *
 * <p>Namespaced by resource to keep the surface tractable:
 * {@code source.videos.*}, {@code source.series.*},
 * {@code source.episodes.*}, {@code source.channels.*},
 * {@code source.categories.*}, plus optional sub-adapters for
 * mutation surfaces ({@code library}, {@code subscriptions}) that
 * hosts can omit when their backend can't write.
 *
 * <p>Library + subscriptions sub-adapters are OPTIONAL because the
 * underlying mutation paths (favorite/unfavorite, subscribe/unsub,
 * watch-history mark) span both our backend and Cinema8 — hosts
 * without one or the other can ship read-only.
 */

// =============================================================================
// Resource shapes — ported from ThemeMaker mediaApi.ts (Cinema8-shaped)
// =============================================================================

/** Lean catalog item — used in rails, search results, channel rails.
 *  Discriminates video vs series via {@code kind}. */
export interface MediaItem {
  id: string;
  slug: string;
  kind?: 'video' | 'series';
  title?: string;
  subtitle?: string;
  kicker?: string;
  imageUrl?: string;
  meta?: string[];
  categorySlugs?: string[];
  /** Shorts hint — rails that mix short + long content (search,
   *  category browse) badge shorts via this. Absent on long-form. */
  isShort?: boolean;
  durationSeconds?: number;
}

/** Full video record. {@code mediaId} is the Cinema8 player media id;
 *  {@code videoUrl} is a direct MP4 URL set on shorts where we
 *  control the asset. */
export interface MediaVideoFull {
  id: string;
  slug: string;
  kind?: 'video';
  mediaId?: string;
  /** Fully-built Cinema8 embed URL for the player iframe. The portal
   *  server builds this from the tenant Cinema8 host + {@code mediaId}
   *  so blocks stay host-agnostic. Absent => player derives from
   *  {@code mediaId} + a default host, or falls back to {@code videoUrl}. */
  playerEmbedUrl?: string;
  title?: string;
  subtitle?: string;
  kicker?: string;
  description?: string;
  director?: string;
  releaseYear?: number;
  durationLabel?: string;
  region?: string;
  language?: string;
  posterUrl?: string;
  airDate?: string;
  tags?: string[];
  categorySlugs?: string[];
  /** Publishing channel — present when the video has a channel
   *  association. Watch page reads to render the channel chip +
   *  Subscribe button. Absent => unaffiliated video. */
  channelSlug?: string;
  channelName?: string;
  channelAvatar?: string;
  channelSubscriberCount?: number;
  /** Shorts metadata. */
  isShort?: boolean;
  durationSeconds?: number;
  /** Direct MP4 URL. Set on shorts; absent on long-form (which plays
   *  via Cinema8 player + {@code mediaId}). */
  videoUrl?: string;
}

export interface MediaVideoDetail extends MediaVideoFull {
  related?: MediaItem[];
}

export interface MediaSeriesFull {
  id: string;
  slug: string;
  kind?: 'series';
  title?: string;
  subtitle?: string;
  kicker?: string;
  description?: string;
  director?: string;
  releaseYear?: number;
  region?: string;
  posterUrl?: string;
  episodeRuntime?: string;
  tags?: string[];
  categorySlugs?: string[];
  /** Cinema8 media id of the trailer (or first episode fallback). */
  mediaId?: string;
}

export interface MediaEpisode extends MediaVideoFull {
  episodeNumber?: number;
  watched?: boolean;
}

export interface MediaSeriesDetail extends MediaSeriesFull {
  episodes?: MediaEpisode[];
  episodeCount?: number;
  related?: MediaItem[];
}

export interface ChannelCard {
  id: string;
  slug: string;
  kind?: 'channel';
  name?: string;
  description?: string;
  avatarUrl?: string;
  subscriberCount?: number;
  videoCount?: number;
  seriesCount?: number;
}

export interface ChannelDetail extends ChannelCard {
  bannerUrl?: string;
  videos?: MediaItem[];
  series?: MediaItem[];
}

export interface MediaCategory {
  slug: string;
  name: string;
  description?: string;
  color?: string;
  count?: number;
}

/** A single comment on a video. Maps the upstream {@code UCommentView}
 *  (id / commentData / owner.name / createdAt) down to the fields the
 *  comments block renders. */
export interface MediaComment {
  id: string;
  /** The comment text ({@code UCommentView.commentData}). */
  body: string;
  /** Display name of the author ({@code owner.name} → {@code createdBy}). */
  author?: string;
  /** ISO timestamp ({@code createdAt}). */
  createdAt?: string;
}

export type ChannelSort = 'top-subscribed' | 'newest' | 'alphabetical';

/** Source slug for catalog rails — host-defined keys. Examples from
 *  the legacy controller: {@code trending}, {@code popular},
 *  {@code editor-picks}, {@code latest}, {@code new-this-week}. */
export type MediaSourceSlug = string;

// =============================================================================
// Sub-namespace contracts
// =============================================================================

/** Video catalog operations. */
export interface MediaVideosNamespace {
  /** Source-keyed rail (trending/popular/editor-picks/latest). */
  fetchSource(opts: {
    source: MediaSourceSlug;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<{ items: MediaItem[]; total: number; label?: string } | null>;

  /** Single video detail by slug. */
  fetchBySlug(slug: string, signal?: AbortSignal): Promise<MediaVideoDetail | null>;

  /** Vertical short-form feed. */
  fetchShorts(opts?: { limit?: number; signal?: AbortSignal }): Promise<MediaVideoFull[]>;

  /** Filtered list (category + query). */
  fetchByCategory(opts: {
    categorySlug?: string;
    q?: string;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<{ items: MediaVideoFull[]; total: number; label: string }>;
}

export interface MediaSeriesNamespace {
  fetchSource(opts: {
    source: MediaSourceSlug;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<{ items: MediaItem[]; total: number; label?: string } | null>;

  fetchBySlug(slug: string, signal?: AbortSignal): Promise<MediaSeriesDetail | null>;
}

export interface MediaChannelsNamespace {
  /** Channel directory ("Discover"). */
  list(opts?: {
    sort?: ChannelSort;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  }): Promise<ChannelCard[]>;

  fetchBySlug(slug: string, signal?: AbortSignal): Promise<ChannelDetail | null>;
}

export interface MediaCategoriesNamespace {
  list(opts?: { signal?: AbortSignal }): Promise<MediaCategory[]>;
}

/** Video comments — list + write. Optional sub-adapter: hosts whose
 *  backend can't serve comments omit it, and the comments block then
 *  renders its adapter-missing placeholder. Keyed by video slug for
 *  read/add (the adapter parses the id off the slug tail); by comment
 *  id for edit/remove. */
export interface MediaCommentsNamespace {
  list(
    videoSlug: string,
    opts?: { limit?: number; offset?: number; signal?: AbortSignal },
  ): Promise<{ items: MediaComment[]; total: number }>;
  /** Post a new comment. Returns the created record, or {@code null}
   *  on failure (optimistic UI rolls back). */
  add(videoSlug: string, text: string): Promise<MediaComment | null>;
  edit(commentId: string, text: string): Promise<boolean>;
  remove(commentId: string): Promise<boolean>;
}

/** Per-user library — favorites + history. Mutation sub-adapter;
 *  hosts whose backend can't write user-state omit this entirely. */
export interface MediaLibraryNamespace {
  fetchWatchLater(signal?: AbortSignal): Promise<{ items: MediaItem[]; skipped: boolean }>;
  fetchHistory(signal?: AbortSignal): Promise<{ items: MediaItem[]; skipped: boolean }>;

  /** Toggle a video into / out of watch-later. Returns the new state
   *  or null on failure (optimistic UI should roll back). */
  toggleWatchLater(opts: {
    videoSlug: string;
  }): Promise<{ favorite?: boolean; skipped?: boolean } | null>;

  /** Fire-and-forget history marker. */
  markHistory(opts: { videoSlug: string }): Promise<void>;
}

/** Channel subscriptions. Same mutation-sub-adapter pattern. */
export interface MediaSubscriptionsNamespace {
  fetchSubscribed(signal?: AbortSignal): Promise<{ items: ChannelCard[]; skipped: boolean }>;
  toggle(opts: {
    channelSlug: string;
  }): Promise<{ subscribed?: boolean; skipped?: boolean } | null>;
}

// =============================================================================
// Adapter contract + Provider
// =============================================================================

export interface MediaSource {
  videos: MediaVideosNamespace;
  series: MediaSeriesNamespace;
  channels: MediaChannelsNamespace;
  categories: MediaCategoriesNamespace;
  /** Optional. Hosts whose backend can't serve video comments omit it. */
  comments?: MediaCommentsNamespace;
  /** Optional. Hosts without writable user library leave undefined. */
  library?: MediaLibraryNamespace;
  /** Optional. Hosts without writable subscriptions leave undefined. */
  subscriptions?: MediaSubscriptionsNamespace;
}

const MediaSourceContext = createContext<MediaSource | null>(null);

export function MediaSourceProvider({
  value,
  children,
}: {
  value: MediaSource;
  children: ReactNode;
}) {
  return <MediaSourceContext.Provider value={value}>{children}</MediaSourceContext.Provider>;
}

export function useMediaSource(): MediaSource | null {
  return useContext(MediaSourceContext);
}
